<script setup lang="ts">
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import { useRefreshStorageAreaStore } from '@/storageAreas/stores/refreshStorageAreaStore';
import { storeToRefs } from 'pinia';
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { useProductStore } from 'shared/products/stores/productStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useTranslations } from 'shared/translations/composables/useTranslations';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRefereshProductGroupStore } from '@/products/groups/commands/refreshProductGroupStore';
import { useThemeStore } from 'shared/themes/stores/themeStore';

const themeStore = useThemeStore();
const storageAreaStore = useStorageAreaStore();
const storageAreas = storeToRefs(storageAreaStore).storageAreas;
const productStore = useProductStore();
const refreshProductStore = useRefereshProductStore();
const productGroupStore = useProductGroupStore();
const refreshProductGroupStore = useRefereshProductGroupStore();
const translations = useTranslations();
const languageStore = useLanguageStore();
const refreshStorageAreaStore = useRefreshStorageAreaStore();
const { loading } = storeToRefs(refreshStorageAreaStore);
const productGroups = ref<ProductGroupTableRow[]>([]);

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

const intervalId = ref<number | null>(null)

onMounted(async () => {
  await refreshStorageAreaStore.initialize();
  await refreshProductStore.initialize();
  await refreshProductGroupStore.initialize();

  updateData();

  intervalId.value = window.setInterval(async () => {
    updateData();
  }, 10000);
});

onUnmounted(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value)
  }
})

const chartData = computed(() => {
  return {
    labels: productGroups.value.map(pg => pg.productGroupName),
    datasets: [
      {
        label: translations.value.generic.numberOfLogs,
        backgroundColor: themeStore.currentTheme == 'SILog' ? "#66BB6A" : '#1E5032',
        data: productGroups.value.map(pg => pg.numberOfLogs),
        yAxisID: 'y1',
      },
      {
        label: translations.value.generic.volume,
        backgroundColor: themeStore.currentTheme == 'SILog' ? "#42A5F5" : '#BEBE1E',
        data: productGroups.value.map(pg => pg.volume),
        yAxisID: 'y2',
      }
    ]
  }
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top' as const,
    },
    title: {
      display: true,
      text: translations.value.dashboard.productGroupDiagram.title,
    },
  },
  scales: {
    y1: {
      type: 'linear' as const,
      display: true,
      position: 'left' as const,
      beginAtZero: true,
      title: {
        display: true,
        text: translations.value.generic.numberOfLogs
      }
    },
    y2: {
      type: 'linear' as const,
      display: true,
      position: 'right' as const,
      beginAtZero: true,
      title: {
        display: true,
        text: translations.value.generic.volume
      },
      grid: {
        drawOnChartArea: false, // only want the grid lines for one axis to show up
      }
    }
  }
};

function updateData() {
  productGroups.value = [];

  for (const storageArea of storageAreas.value) {
    if (!storageArea.logYardData) continue;
    for (const content of storageArea.logYardData.content) {
      const product = productStore.products.find(p => p.id === content.productId);
      if (!product) continue;

      const productGroup = productGroupStore.groups.find(pg => pg.id === product.productGroupId);
      let productGroupName = translations.value.generic.unknown;
      let productGroupColor = '#000000';
      if (productGroup) {
        productGroupName = productGroup.name;
        if (productGroup.color) {
          productGroupColor = productGroup.color;
        }
      }
      const existing = productGroups.value.find(pg => pg.productGroupName === productGroupName);
      if (existing) {
        existing.numberOfLogs += content.numberOfLogs;
        existing.volume += content.volume;
      } else {
        productGroups.value.push({
          productGroupName: productGroupName,
          numberOfLogs: content.numberOfLogs,
          volume: content.volume,
          color: productGroupColor,
        });
      }
    }
  }

  productGroups.value = productGroups.value.sort((a, b) => {
    if (a.productGroupName === translations.value.generic.unknown) return 1;
    if (b.productGroupName === translations.value.generic.unknown) return -1;
    return a.productGroupName.localeCompare(b.productGroupName);
  });
}

interface ProductGroupTableRow {
  productGroupName: string;
  numberOfLogs: number;
  volume: number;
  color: string;
}


</script>

<template>
  <Bar id="my-chart-id" :options="chartOptions" :data="chartData" />
</template>
